import { Box, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { lang } from "../../Utils/lang";

type PasswordStrengthMeterProps = {
  password: string;
};

type Requirement = {
  label: string;
  test: (value: string) => boolean;
};

const REQUIREMENTS: Requirement[] = [
  { label: lang.passwordReqLength, test: (value) => value.length >= 8 },
  { label: lang.passwordReqUppercase, test: (value) => /[A-Z]/.test(value) },
  { label: lang.passwordReqLowercase, test: (value) => /[a-z]/.test(value) },
  { label: lang.passwordReqDigit, test: (value) => /\d/.test(value) },
];

const STRENGTH_COLORS = ["#eb2828", "#eb2828", "#e0a800", "#c7c11a", "#359d35"];
const STRENGTH_LABELS = [
  lang.passwordStrengthWeak,
  lang.passwordStrengthWeak,
  lang.passwordStrengthFair,
  lang.passwordStrengthGood,
  lang.passwordStrengthStrong,
];

export default function PasswordStrengthMeter(props: PasswordStrengthMeterProps) {
  const { password } = props;

  if (!password) return null;

  const metFlags = REQUIREMENTS.map((requirement) => requirement.test(password));
  const score = metFlags.filter(Boolean).length;
  const color = STRENGTH_COLORS[score];
  const label = STRENGTH_LABELS[score];

  return (
    <Box sx={{ marginTop: -1, marginBottom: 2 }} aria-live="polite">
      <Box
        sx={{
          height: 4,
          borderRadius: 2,
          backgroundColor: "#e0e0e0",
          overflow: "hidden",
          marginBottom: 0.5,
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${(score / REQUIREMENTS.length) * 100}%`,
            backgroundColor: color,
            borderRadius: 2,
            "@media (prefers-reduced-motion: no-preference)": {
              transition: "width 200ms ease, background-color 200ms ease",
            },
          }}
        />
      </Box>
      <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
        {label}
      </Typography>
      <Box
        component="ul"
        sx={{
          listStyle: "none",
          margin: "6px 0 0",
          padding: 0,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          columnGap: "12px",
          rowGap: "2px",
        }}
      >
        {REQUIREMENTS.map((requirement, index) => {
          const met = metFlags[index];
          return (
            <Box
              component="li"
              key={requirement.label}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {met ? (
                <CheckCircleIcon sx={{ fontSize: 15, color: "#359d35" }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: 15, color: "#c4c4c4" }} />
              )}
              <Typography
                variant="caption"
                sx={{ color: met ? "text.primary" : "text.secondary" }}
              >
                {requirement.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
